import React, { useState, useEffect } from 'react';
import { Toast, Button, Dialog, NavBar, List, Ellipsis, Space, DotLoading, Result } from 'antd-mobile';
import { useParams, useNavigate } from 'react-router-dom';
import { useZone } from '../contexts/ZoneContext';
import { getZoneApi, api } from '../api'; // 导入API客户端
import './MobileMorePage.css';

/**
 * 移动端更多页面组件
 * 包含活动管理、分区管理、设置和关于四个标签页
 */
const MobileMorePage = () => {
  const [activeTab, setActiveTab] = useState('manage');
  const [settledEvents, setSettledEvents] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const { zone } = useParams(); // 获取URL中的区域参数
  const { currentZone } = useZone(); // 从上下文获取当前区域
  const navigate = useNavigate();
  
  // 确保有区域信息
  const activeZone = zone || currentZone || 'beijing';
  // 获取区域API客户端，确保传入的zone值是有效字符串
  const zoneApi = getZoneApi(typeof activeZone === 'string' ? activeZone : 'beijing');
  
  // 安全地访问properties
  const safelyAccess = (obj, path, defaultValue = '') => {
    if (!obj) return defaultValue;
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result == null || typeof result !== 'object') return defaultValue;
      result = result[key];
    }
    return result === undefined || result === null ? defaultValue : result;
  };

  // 未使用的函数，已注释
  // const safeStringify = (obj) => {
  //   if (obj === null || obj === undefined) return '';
  //   try {
  //     return typeof obj === 'string' ? obj : JSON.stringify(obj);
  //   } catch (error) {
  //     return '';
  //   }
  // };
  
  // 获取已结算活动列表
  const fetchSettledEvents = async () => {
    try {
      setLoading(true);
      console.log(`获取 ${activeZone} 区域的已结算活动...`);
      const response = await zoneApi.events.getSettled();
      if (response.success) {
        console.log('已结算活动数据:', response.data);
        setSettledEvents(response.data);
      } else {
        Toast.show({
          content: '获取已结算活动失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('获取已结算活动出错:', error);
      Toast.show({
        content: '获取数据时出错',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // 重置活动结算状态
  const resetEvent = async (eventId) => {
    try {
      setLoading(true);
      const response = await zoneApi.events.reset(eventId);
      if (response.success) {
        Toast.show({
          content: '重置成功',
          position: 'bottom',
        });
        // 刷新活动列表
        fetchSettledEvents();
      } else {
        Toast.show({
          content: '重置失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('重置活动出错:', error);
      Toast.show({
        content: '重置活动时出错',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  // 删除活动总结（仅删除AI总结内容，不影响活动结算数据）
  const deleteEventSummary = (eventId) => {
    Dialog.confirm({
      title: '确认删除总结',
      content: '确定要删除该活动的AI总结吗？删除后可再次生成。',
      confirmText: '删除',
      cancelText: '取消',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await zoneApi.events.summary.delete(eventId);
          if (response.status === 'success') {
            Toast.show({
              content: '总结已删除',
              position: 'bottom'
            });
            fetchSettledEvents();
          } else {
            Toast.show({
              content: response.message || '删除总结失败',
              position: 'bottom'
            });
          }
        } catch (error) {
          console.error('删除总结出错:', error);
          Toast.show({
            content: error.response?.data?.message || '删除总结时出错',
            position: 'bottom'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  // 重置所有球员的统计数据
  const resetAllPlayersStats = async () => {
    try {
      const response = await zoneApi.players.resetStats();
      if (response.success) {
        Toast.show({
          content: response.data.message,
          position: 'bottom',
        });
      } else {
        Toast.show({
          content: '重置球员数据失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('重置球员数据出错:', error);
      Toast.show({
        content: '重置球员数据时出错',
        position: 'bottom',
      });
    }
  };
  
  // 确认重置所有球员的统计数据
  const confirmResetAllPlayersStats = () => {
    Dialog.confirm({
      title: '确认重置',
      content: '确定要重置所有球员的统计数据吗？这将清除所有球员的积分、胜负场数等统计数据，但保留球员基本信息。',
      confirmText: '重置',
      cancelText: '取消',
      onConfirm: resetAllPlayersStats,
    });
  };
  
  // 获取所有分区列表
  const fetchZones = async () => {
    try {
      setLoading(true);
      console.log('获取所有分区列表，包括已删除分区...');
      // 使用api对象中的system方法获取分区列表
      const response = await api.system.getAllZones();
      if (response.status === 'success') {
        console.log('分区列表数据:', response.data.zones);
        setZones(response.data.zones || []);
      } else {
        Toast.show({
          content: '获取分区列表失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('获取分区列表出错:', error);
      Toast.show({
        content: '获取分区数据时出错',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // 软删除分区
  const softDeleteZone = async (zoneCode) => {
    try {
      setLoading(true);
      console.log(`软删除分区: ${zoneCode}`);
      // 如果API客户端中没有这个方法，可以创建一个临时的axios请求
      const axios = require('axios');
      const { getApiBaseUrl } = require('../utils/apiConfig');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await axios.delete(`${API_BASE_URL}/api/zones/${zoneCode}/soft`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = response.data;
      if (data.status === 'success') {
        Toast.show({
          content: data.message || '分区删除成功',
          position: 'bottom',
        });
        // 刷新分区列表
        fetchZones();
      } else {
        Toast.show({
          content: data.message || '分区删除失败',
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('删除分区出错:', error);
      Toast.show({
        content: '删除分区时出错',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // 确认删除分区
  const confirmSoftDeleteZone = (zone) => {
    if (!zone) return;
    
    Dialog.confirm({
      title: '确认删除',
      content: `确定要删除分区 "${safelyAccess(zone, 'name', '未命名')}" 吗？这将标记分区为已删除状态，并备份数据。`,
      confirmText: '删除',
      cancelText: '取消',
      onConfirm: () => softDeleteZone(safelyAccess(zone, 'code', '')),
    });
  };
  
  // 初始化时获取已结算活动
  useEffect(() => {
    if (activeTab === 'manage') {
      fetchSettledEvents();
    } else if (activeTab === 'zones') {
      fetchZones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeZone]);
  
  // 渲染已结算活动列表
  const renderSettledEvents = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Space style={{ fontSize: '16px' }}>
            加载中<DotLoading />
          </Space>
        </div>
      );
    }
    
    if (!settledEvents || settledEvents.length === 0) {
      return (
        <Result
          status="waiting"
          title="暂无已结算活动"
          description="所有活动都未结算或已被重置"
        />
      );
    }
    
    return (
      <List header="已结算活动列表">
        {settledEvents.map(event => (
          <List.Item 
            key={event._id || Math.random().toString()}
            extra={
              <Space>
                <Button
                  size="small"
                  fill="outline"
                  disabled={loading}
                  onClick={() => deleteEventSummary(event._id)}
                >
                  删除总结
                </Button>
                <Button 
                  color="danger" 
                  size="small"
                  disabled={loading}
                  onClick={() => resetEvent(event._id)}
                >
                  重置
                </Button>
              </Space>
            }
          >
            <div className="event-card-content">
              <div className="event-name">
                <Ellipsis direction="end" content={event.name || '未命名活动'} />
              </div>
              <div className="event-info">
                <div className="event-date">
                  {event.date ? new Date(event.date).toLocaleDateString() : '未知日期'}
                </div>
                <div className="event-stats">
                  {(event.matchCount || 0)} 场比赛 | {(event.playerCount || 0)} 位球员
                </div>
              </div>
            </div>
          </List.Item>
        ))}
      </List>
    );
  };
  
  // 渲染分区列表
  const renderZones = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Space style={{ fontSize: '16px' }}>
            加载中<DotLoading />
          </Space>
        </div>
      );
    }
    
    if (!zones || zones.length === 0) {
      return (
        <Result
          status="waiting"
          title="暂无分区数据"
          description="未找到任何分区信息"
        />
      );
    }
    
    // 对分区进行排序，未删除的排在前面
    const sortedZones = [...zones].sort((a, b) => {
      // 首先按照是否删除排序
      if ((a.isDeleted || false) !== (b.isDeleted || false)) {
        return (a.isDeleted || false) ? 1 : -1;
      }
      // 然后按照order排序
      return (a.order || 0) - (b.order || 0);
    });
    
    return (
      <List header="分区列表">
        {sortedZones.map((zone) => (
          <List.Item
            key={safelyAccess(zone, 'id', '') || safelyAccess(zone, '_id', '') || Math.random().toString()}
            arrow={false}
            className={safelyAccess(zone, 'isDeleted', false) ? 'zone-item-deleted' : 'zone-item'}
            onClick={() => {
              // 暂无特定操作，也可以添加点击跳转到分区详情
            }}
            description={
              <div className="zone-info">
                <div className="zone-code">编码: {safelyAccess(zone, 'code', '未知')}</div>
                {safelyAccess(zone, 'description', '') && (
                  <div className="zone-desc">
                    <Ellipsis direction="end" content={safelyAccess(zone, 'description', '')} />
                  </div>
                )}
                {safelyAccess(zone, 'isDeleted', false) && (
                  <div className="zone-deleted-info">
                    <div className="zone-deleted-time">
                      删除时间: {safelyAccess(zone, 'deletedAt', '') 
                        ? new Date(safelyAccess(zone, 'deletedAt', '')).toLocaleString() 
                        : '未知'}
                    </div>
                    {safelyAccess(zone, 'backupLocation', '') && (
                      <div className="zone-backup">备份位置: {safelyAccess(zone, 'backupLocation', '')}</div>
                    )}
                  </div>
                )}
              </div>
            }
            extra={
              <div className="zone-actions">
                <div className="zone-status">
                  {safelyAccess(zone, 'isDeleted', false) ? (
                    <span className="zone-deleted">已删除</span>
                  ) : safelyAccess(zone, 'active', false) ? (
                    <span className="zone-active">已启用</span>
                  ) : (
                    <span className="zone-inactive">已停用</span>
                  )}
                  {safelyAccess(zone, 'isDefault', false) && <span className="zone-default">默认</span>}
                </div>
                {!safelyAccess(zone, 'isDeleted', false) && !safelyAccess(zone, 'isDefault', false) && (
                  <Button
                    size="mini"
                    color="danger"
                    className="zone-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmSoftDeleteZone(zone);
                    }}
                  >
                    删除
                  </Button>
                )}
              </div>
            }
          >
            <div className="zone-name">
              {safelyAccess(zone, 'isDeleted', false) && <span className="zone-deleted-tag">已删除</span>}
              <Ellipsis direction="end" content={safelyAccess(zone, 'name', '未命名分区')} />
            </div>
          </List.Item>
        ))}
      </List>
    );
  };

  // 重新统计所有已结算活动的比赛
  const handleRecalculateStatistics = async () => {
    Dialog.confirm({
      title: '确认重新统计',
      content: '此操作将重新统计所有已结算活动中的比赛数据，可能需要较长时间。是否继续？',
      onConfirm: async () => {
        try {
          setLoading(true);
              const response = await zoneApi.statistics.recalculate();
              if (response.success) {
                Toast.show({
                  content: `重新统计完成！处理了 ${response.data.matchesProcessed} 场比赛，更新了 ${response.data.playersUpdated} 个球员`,
                  duration: 3000
                });
              } else {
                Toast.show({
                  content: response.message || '重新统计失败',
                  duration: 2000
                });
              }
            } catch (error) {
              console.error('重新统计失败:', error);
              Toast.show({
                content: error.response?.data?.message || error.message || '重新统计失败，请稍后重试',
                duration: 2000
              });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 清除所有分析数据
  const handleClearStatistics = async () => {
    Dialog.confirm({
      title: '确认清除分析',
      content: '此操作将清除所有球员详情中的分析数据（搭档分析、对手分析、得分统计等），但会保留积分和比赛数据。是否继续？',
      confirmText: '确认清除',
      cancelText: '取消',
      onConfirm: async () => {
        // 二次确认
        Dialog.confirm({
          title: '最后确认',
          content: '您确定要清除所有分析数据吗？此操作会将所有活动标记为未结算，分析数据将不再显示。',
          confirmText: '确定清除',
          cancelText: '取消',
          onConfirm: async () => {
            try {
              setLoading(true);
              const response = await zoneApi.statistics.clear();
              if (response.success) {
                Toast.show({
                  content: `清除分析完成！已将 ${response.data.eventsUpdated} 个活动标记为未结算`,
                  duration: 3000
                });
                // 刷新已结算活动列表
                fetchSettledEvents();
              } else {
                Toast.show({
                  content: response.message || '清除分析失败',
                  duration: 2000
                });
              }
            } catch (error) {
              console.error('清除分析失败:', error);
              Toast.show({
                content: error.response?.data?.message || error.message || '清除分析失败，请稍后重试',
                duration: 2000
              });
            } finally {
              setLoading(false);
            }
          }
        });
      }
    });
  };

  // 渲染统计管理功能
  const renderStatisticsManagement = () => {
    return (
      <div className="statistics-management-section">
        <List header="统计管理">
          <List.Item
            onClick={handleRecalculateStatistics}
            disabled={loading}
            extra={loading ? <DotLoading /> : null}
          >
            <div className="statistics-action">
              <div className="action-title">重新统计所有已结算活动</div>
              <div className="action-desc">对所有已结算活动中的比赛进行重新统计</div>
            </div>
          </List.Item>
          <List.Item
            onClick={handleClearStatistics}
            disabled={loading}
            extra={loading ? <DotLoading /> : null}
          >
            <div className="statistics-action">
              <div className="action-title">清除所有分析数据</div>
              <div className="action-desc">清除球员详情中的分析数据（保留积分和比赛数据）</div>
            </div>
          </List.Item>
          <List.Item
            onClick={handleRestoreAnalysis}
            disabled={loading}
            extra={loading ? <DotLoading /> : null}
          >
            <div className="statistics-action">
              <div className="action-title">恢复分析数据</div>
              <div className="action-desc">重新启用球员详情中的分析数据</div>
            </div>
          </List.Item>
        </List>
      </div>
    );
  };

  // 恢复分析数据
  const handleRestoreAnalysis = async () => {
    Dialog.confirm({
      title: '确认恢复分析',
      content: '此操作将重新启用所有球员详情中的分析数据（搭档分析、对手分析、得分统计等）。是否继续？',
      confirmText: '确认恢复',
      cancelText: '取消',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await zoneApi.statistics.restore();
          if (response.success) {
            Toast.show({
              content: '恢复分析完成！分析数据已重新启用',
              duration: 3000
            });
          } else {
            Toast.show({
              content: response.message || '恢复分析失败',
              duration: 2000
            });
          }
        } catch (error) {
          console.error('恢复分析失败:', error);
          Toast.show({
            content: error.response?.data?.message || error.message || '恢复分析失败，请稍后重试',
            duration: 2000
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };
  
  // 渲染设置选项
  const renderSettingsOptions = () => {
    return (
      <List header="系统设置">
        <List.Item 
          onClick={() => navigate(`/badminton/${activeZone}/players`)}
        >
          球员管理
        </List.Item>
        <List.Item 
          onClick={() => Toast.show('系统设置功能开发中')}
        >
          系统设置
        </List.Item>
        <List.Item 
          onClick={confirmResetAllPlayersStats}
        >
          重置球员数据
        </List.Item>
        <List.Item 
          onClick={() => {
            Dialog.confirm({
              title: '确认重置',
              content: '确定要重置所有数据吗？这将清除所有比赛记录和积分变化。',
              confirmText: '重置',
              cancelText: '取消',
              onConfirm: async () => {
                try {
                  const response = await zoneApi.events.resetAll();
                  if (response.success) {
                    Toast.show({
                      content: '所有数据已重置',
                      position: 'bottom',
                    });
                  }
                } catch (error) {
                  console.error('重置数据出错:', error);
                  Toast.show({
                    content: '重置数据时出错',
                    position: 'bottom',
                  });
                }
              },
            });
          }}
        >
          重置所有数据
        </List.Item>
      </List>
    );
  };
  
  // 渲染关于信息
  const renderAboutInfo = () => {
    return (
      <div className="about-content">
        <Result
          status="success"
          title="羽毛球积分系统"
          description="版本 1.2.1"
        />
        <List header="关于系统">
          <List.Item>本系统用于管理羽毛球比赛和积分排名</List.Item>
          <List.Item>采用Elo积分系统计算球员单打和双打积分</List.Item>
          <List.Item>提供活动管理、比赛记录、球员管理和积分统计功能</List.Item>
        </List>
      </div>
    );
  };
  
  return (
    <div className="mobile-more-page">
      <NavBar back={null}>更多功能</NavBar>
      
      <div className="tab-bar">
        <div 
          className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          活动管理
        </div>
        <div 
          className={`tab ${activeTab === 'zones' ? 'active' : ''}`}
          onClick={() => setActiveTab('zones')}
        >
          分区管理
        </div>
        <div 
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          设置
        </div>
        <div 
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          关于
        </div>
      </div>
      
      <div className="more-content">
        {activeTab === 'manage' && (
          <div className="section-manage">
            <div className="section-header">已结算活动管理</div>
            {renderSettledEvents()}
          </div>
        )}
        
        {activeTab === 'zones' && (
          <div className="section-zones">
            <div className="section-header">分区管理</div>
            {renderZones()}
            {renderStatisticsManagement()}
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="section-settings">
            <div className="section-header">设置</div>
            {renderSettingsOptions()}
          </div>
        )}
        
        {activeTab === 'about' && (
          <div className="section-about">
            <div className="section-header">关于系统</div>
            {renderAboutInfo()}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMorePage; 